<template>
  <div>
    <section class="relative md:py-24 py-16 overflow-hidden" id="features">

      <!-- Background decorations -->
      <div class="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
        <div class="absolute top-20 left-10 w-72 h-72 bg-orange-100 dark:bg-orange-900/10 rounded-full blur-3xl opacity-50"></div>
        <div class="absolute bottom-20 right-10 w-96 h-96 bg-amber-50 dark:bg-amber-900/5 rounded-full blur-3xl opacity-60"></div>
      </div>

      <div class="container lg mx-auto relative z-10">
        <!-- Section Header -->
        <div class="grid grid-cols-1 lg:grid-cols-2 md:grid-cols-2 pb-8 items-center">
          <div class="section-reveal-left">
            <div class="inline-flex items-center gap-2 mb-3">
              <div class="h-px w-8 bg-orange-600"></div>
              <h6 class="text-orange-600 text-sm font-semibold uppercase tracking-widest">What We Do ?</h6>
            </div>
            <h3 class="mb-4 md:text-4xl text-3xl font-bold dark:text-white md:mb-0 leading-tight">
              Perfect Solution For Your<br>
              <span class="text-orange-600">Luxury Fragrance</span> Needs
            </h3>
          </div>

          <div class="section-reveal-right">
            <p class="text-slate-400 dark:text-slate-300 max-w-xl text-base leading-relaxed lg:ps-8">
              A palace of pure perfume extracts designed for those who appreciate true quality. Every drop crafted with passion and tradition.
            </p>
          </div>
        </div><!-- end grid -->

        <!-- Service Cards -->
        <div class="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2 gap-6">
          <div
            v-for="(item, index) in services"
            :key="index"
            class="service-card"
            :style="{ transitionDelay: `${index * 0.1}s` }"
          >
            <!-- Glowing icon -->
            <div class="icon-wrap w-16 h-16 rounded-2xl flex items-center justify-center text-2xl mb-6 relative overflow-hidden">
              <div class="icon-bg absolute inset-0 bg-orange-600/10 transition-all duration-500"></div>
              <i :class="`uil uil-${item.logo} relative z-10 text-orange-600`"></i>
            </div>

            <div class="content">
              <h4 class="text-base font-bold text-slate-800 dark:text-white mb-3 hover:text-orange-600 dark:hover:text-orange-500 transition-colors duration-300">
                {{ item.title }}
              </h4>
              <p class="text-slate-400 dark:text-slate-400 text-sm leading-relaxed">{{ item.desc }}</p>
            </div>

            <!-- Hover accent line -->
            <div class="mt-6 h-0.5 w-0 group-hover:w-full bg-gradient-to-r from-orange-600 to-amber-400 transition-all duration-500 rounded-full card-line"></div>
          </div>
        </div><!-- end grid -->
      </div><!-- end container -->

      <!-- Work Process -->
      <div class="container md:mt-24 mt-16 relative z-10">
        <div class="grid grid-cols-1 pb-12 text-center">
          <div class="process-reveal">
            <div class="inline-flex items-center gap-2 justify-center mb-3">
              <div class="h-px w-8 bg-orange-600"></div>
              <h6 class="text-orange-600 text-sm font-semibold uppercase tracking-widest">Work Process</h6>
              <div class="h-px w-8 bg-orange-600"></div>
            </div>
            <h3 class="mb-4 md:text-4xl text-3xl font-bold dark:text-white leading-tight">
              How We Create Our<br><span class="text-orange-600">Natural Fragrance</span> Extracts
            </h3>
            <p class="text-slate-400 dark:text-slate-300 max-w-xl mx-auto leading-relaxed">
              From growing our own flowers to extracting and storing pure oils, our process reflects the beauty and traditions of Siwa.
            </p>
          </div>
        </div>

        <!-- Timeline steps -->
        <div class="relative">
          <!-- Vertical connector line -->
          <div class="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-orange-300 via-orange-500 to-transparent hidden md:block transform -translate-x-1/2 timeline-line"></div>

          <div v-for="(step, index) in processSteps" :key="index"
               class="timeline-step relative mb-12"
               :style="{ transitionDelay: `${index * 0.15}s` }">
            <div class="grid sm:grid-cols-2 gap-8 items-center">

              <!-- Left content or empty -->
              <div :class="index % 2 === 0 ? 'sm:text-right' : 'sm:order-2'">
                <div v-if="index % 2 === 0" class="timeline-content-box sm:pe-10">
                  <span class="inline-block text-xs font-semibold uppercase tracking-widest text-orange-500 mb-2">Step {{ index + 1 }}</span>
                  <h5 class="text-xl font-bold dark:text-white mb-3">{{ step.title }}</h5>
                  <p class="text-slate-400 leading-relaxed text-sm">{{ step.desc }}</p>
                </div>
              </div>

              <!-- Center dot -->
              <div class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:flex z-10">
                <div class="step-dot w-12 h-12 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-sm shadow-lg shadow-orange-600/40">
                  {{ index + 1 }}
                </div>
                <div class="absolute inset-0 rounded-full bg-orange-600 animate-ping opacity-25"></div>
              </div>

              <!-- Right content or empty -->
              <div :class="index % 2 === 1 ? '' : 'sm:order-2'">
                <div v-if="index % 2 === 1" class="timeline-content-box sm:ps-10">
                  <span class="inline-block text-xs font-semibold uppercase tracking-widest text-orange-500 mb-2">Step {{ index + 1 }}</span>
                  <h5 class="text-xl font-bold dark:text-white mb-3">{{ step.title }}</h5>
                  <p class="text-slate-400 leading-relaxed text-sm">{{ step.desc }}</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div><!-- end container -->
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";

const services = ref([
  {
    logo: "flask",
    title: "Premium Raw Perfume Extracts",
    desc: "Pure, high-quality perfume concentrates sourced and crafted to perfection.",
  },
  {
    logo: "tear",
    title: "Natural Essential & Aroma Oils",
    desc: "Therapeutic-grade oils ideal for wellness, relaxation, and aromatherapy use.",
  },
  {
    logo: "cube",
    title: "Handmade Pyrex Bottles",
    desc: "Luxury, handcrafted Pyrex bottles designed for fragrance storage and gifting.",
  },
  {
    logo: "heart-medical",
    title: "Healing Aroma Solutions",
    desc: "Special blends formulated to support mood, focus, and emotional balance.",
  },
  {
    logo: "sliders-v",
    title: "Custom Fragrance Blending",
    desc: "Create your own unique scent with expertly curated essential extracts.",
  },
  {
    logo: "flower",
    title: "Natural Botanical Extracts",
    desc: "High-purity floral and herbal extracts used in perfumes, skincare, and incense.",
  },
]);

const processSteps = ref([
  {
    title: "Growing the Flowers",
    desc: "Our journey begins in the fields of Siwa. We plant and care for natural flowers and herbs using traditional Bedouin methods to ensure pure and healthy plants.",
  },
  {
    title: "Harvesting by Hand",
    desc: "When the flowers reach their peak freshness, we collect them by hand. This careful harvesting protects the natural aroma and keeps every flower at its best.",
  },
  {
    title: "Extraction & Storage",
    desc: "We gently extract the oils using traditional techniques, then store the pure fragrance extracts in handmade Pyrex bottles to keep their natural quality and scent.",
  },
]);

let observer;
onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('revealed');
      }
    });
  }, { threshold: 0.1 });

  document.querySelectorAll(
    '.section-reveal-left, .section-reveal-right, .service-card, .process-reveal, .timeline-step'
  ).forEach(el => observer.observe(el));
});

onUnmounted(() => {
  if (observer) observer.disconnect();
});
</script>

<style scoped>
/* Section reveals */
.section-reveal-left {
  opacity: 0;
  transform: translateX(-40px);
  transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.section-reveal-right {
  opacity: 0;
  transform: translateX(40px);
  transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s;
}
.section-reveal-left.revealed,
.section-reveal-right.revealed {
  opacity: 1;
  transform: translateX(0);
}

/* Service Cards */
.service-card {
  opacity: 0;
  transform: translateY(40px) scale(0.97);
  transition: opacity 0.6s ease, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease;
  padding: 1.75rem;
  border-radius: 1.5rem;
  border: 1px solid transparent;
  background: #fff;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

:root.dark .service-card {
  background: #1e293b;
  border-color: rgba(255,255,255,0.05);
}

.service-card.revealed {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.service-card:hover {
  box-shadow: 0 20px 60px rgba(234, 88, 12, 0.12);
  border-color: rgba(234, 88, 12, 0.2);
  transform: translateY(-6px) scale(1.01);
}

.service-card:hover .icon-bg {
  background: rgba(234, 88, 12, 0.2);
  transform: scale(1.2);
}

.service-card:hover .card-line {
  width: 100%;
}

.icon-wrap {
  transition: transform 0.3s ease;
}
.service-card:hover .icon-wrap {
  transform: rotate(-5deg) scale(1.1);
}

.card-line {
  transition: width 0.5s ease;
}

/* Process reveal */
.process-reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}
.process-reveal.revealed {
  opacity: 1;
  transform: translateY(0);
}

/* Timeline */
.timeline-step {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.6s ease, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.timeline-step.revealed {
  opacity: 1;
  transform: translateY(0);
}
.timeline-content-box {
  padding: 1.5rem;
  border-radius: 1.25rem;
  background: #fff;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  border: 1px solid rgba(234, 88, 12, 0.1);
  transition: all 0.3s ease;
}
.timeline-content-box:hover {
  box-shadow: 0 8px 30px rgba(234, 88, 12, 0.12);
  border-color: rgba(234, 88, 12, 0.3);
}

:root.dark .timeline-content-box {
  background: #1e293b;
  border-color: rgba(255,255,255,0.05);
}

.step-dot {
  transition: transform 0.3s ease;
}
.step-dot:hover {
  transform: scale(1.2);
}
</style>